import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { haptics } from '../lib/platform';
import { useMedia } from '../lib/useMedia';
import { IconButton } from './Button';
import s from './Sheet.module.css';

/**
 * Bottom sheet on phones, side panel on desktop. Springs in, follows your finger when
 * dragged down from the top, and plays its exit before unmounting.
 */

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Shown at the right of the header, e.g. an Edit button. */
  actions?: ReactNode;
  children: ReactNode;
  /** 'auto' fits content; 'full' fills most of the screen (editors). */
  size?: 'auto' | 'full';
  /** Label for screen readers when there is no visible title. */
  label?: string;
}

const EXIT_MS = 220;

export function Sheet({
  open,
  onClose,
  title,
  actions,
  children,
  size = 'auto',
  label,
}: SheetProps) {
  const desktop = useMedia('(min-width: 1100px)');
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const lastFocus = useRef<Element | null>(null);
  const drag = useRef<{ startY: number; dy: number; t: number } | null>(null);
  const titleId = useId();

  useLayoutEffect(() => {
    if (open) {
      lastFocus.current = document.activeElement;
      setMounted(true);
    } else if (mounted) {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), EXIT_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, mounted]);

  useEffect(() => {
    if (!mounted || !open) return;
    // Next frame so the entering transform animates.
    const r = requestAnimationFrame(() => setVisible(true));
    const el = panel.current;
    el?.focus({ preventScroll: true });
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(r);
      document.body.style.overflow = prevOverflow;
      if (lastFocus.current instanceof HTMLElement)
        lastFocus.current.focus({ preventScroll: true });
    };
  }, [mounted, open]);

  const onKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Tab' && panel.current) {
        const focusables = panel.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  // Drag down to dismiss (phones only), starting from the grabber/header or from content at scrollTop 0.
  const onPointerDown = (e: React.PointerEvent) => {
    if (desktop || e.pointerType === 'mouse') return;
    const fromHeader = (e.target as HTMLElement).closest('[data-grab]');
    if (!fromHeader && (body.current?.scrollTop ?? 0) > 0) return;
    drag.current = { startY: e.clientY, dy: 0, t: performance.now() };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || !panel.current) return;
    d.dy = Math.max(0, e.clientY - d.startY);
    if (d.dy > 4) {
      panel.current.style.transition = 'none';
      panel.current.style.transform = `translateY(${d.dy}px)`;
    }
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || !panel.current) return;
    const velocity = d.dy / Math.max(1, performance.now() - d.t);
    panel.current.style.transition = '';
    panel.current.style.transform = '';
    if (d.dy > 120 || (d.dy > 40 && velocity > 0.6)) {
      void haptics.impact('light');
      onClose();
    }
  };

  if (!mounted) return null;
  return createPortal(
    <div className={[s.root, desktop ? s.desktop : s.phone, visible ? s.in : ''].join(' ')}>
      <div className={s.scrim} onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        className={[s.panel, size === 'full' ? s.full : ''].join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : label}
        tabIndex={-1}
        onKeyDown={onKey}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className={s.header} data-grab>
          {!desktop && <div className={s.grabber} aria-hidden="true" />}
          <div className={s.headerRow}>
            {title ? (
              <h2 id={titleId} className={`${s.title} t-headline`}>
                {title}
              </h2>
            ) : (
              <span />
            )}
            <div className={s.actions}>
              {actions}
              <IconButton icon="x" label="Close" onClick={onClose} />
            </div>
          </div>
        </div>
        <div ref={body} className={s.body}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
